import type { ConditionAnalysisNode } from "./conditionAnalysisSelectors";
import type { ReportPick } from "./reportPickListStorage";
import {
  formatDirectionBarsWindow,
  MARKET_REGIME_DIRECTION_DEADBAND_PCT,
  marketCandleDirectionLabel,
} from "./marketRegimeDirectionLookbacks";

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

export function toNaturalMetricDefinition(payload: unknown): string {
  if (payload == null) return "No definition available.";
  if (typeof payload === "string") return payload;
  if (typeof payload === "number" || typeof payload === "boolean") return String(payload);
  if (!isPlainRecord(payload)) return String(payload);

  const metricText = typeof payload["metric"] === "string" ? payload["metric"].trim() : "";
  const definitionText =
    typeof payload["definition"] === "string"
      ? payload["definition"].trim()
      : typeof payload["description"] === "string"
        ? payload["description"].trim()
        : typeof payload["meaning"] === "string"
          ? payload["meaning"].trim()
          : "";

  const bucketText = isPlainRecord(payload["buckets"])
    ? Object.entries(payload["buckets"])
        .map(([bucket, details]) => {
          if (!isPlainRecord(details)) return null;
          const meaning = typeof details["meaning"] === "string" ? details["meaning"].trim() : "";
          const rule = typeof details["rule"] === "string" ? details["rule"].trim() : "";
          const clause = meaning || rule;
          if (!clause) return null;
          return `${bucket.replace(/_/g, " ")}: ${clause}`;
        })
        .filter((v): v is string => Boolean(v))
        .join(" ")
    : "";

  return [metricText, definitionText, bucketText].filter(Boolean).join(" ").trim() || "No definition available.";
}

/** Always-available glossary entries for market-context dimensions (TF-scaled bar windows). */
export function sliceDimensionDefinitionFallbacks(
  candleTimeframe?: string | null
): Record<string, string> {
  const long = marketCandleDirectionLabel("long", candleTimeframe);
  const short = marketCandleDirectionLabel("short", candleTimeframe);
  const deadband = MARKET_REGIME_DIRECTION_DEADBAND_PCT;
  const dirRule = `net % change over the window, labeled up/down/flat (|Δ%| < ${deadband} → flat)`;
  return {
    market_condition:
      "Regime at trade entry (for example uptrend, downtrend, range, choppy). Classifies broad market behavior context.",
    regime:
      "Alias of market_condition. Regime at trade entry (for example uptrend, downtrend, range, choppy).",
    candle_direction: `${long} at entry: ${dirRule}.`,
    candle_direction_short: `${short} at entry: ${dirRule}.`,
    trade_direction: "Your executed side at entry (long or short).",
    session:
      "Trading session at entry (Tokyo, London, overlaps, etc.). Full cohort slices use stats.condition_analysis.regime_breakdown.by_session; canonical ids on trades: session_at_entry.session_id and meta.session_contract.",
    direction: "Alias of trade_direction (long or short).",
    market_condition_and_direction: `Combined slice at entry: regime + ${long}.`,
    market_condition_and_direction_short: `Combined slice at entry: regime + ${short}.`,
    candle_direction_and_trade_direction: `Combined slice at entry: ${long} + trade direction.`,
    candle_direction_short_and_trade_direction: `Combined slice at entry: ${short} + trade direction.`,
    market_condition_candle_direction_and_trade_direction: `Combined 3-way slice at entry: regime + ${long} + trade direction.`,
    market_condition_candle_direction_short_and_trade_direction: `Combined 3-way slice at entry: regime + ${short} + trade direction.`,
    market_condition_and_direction_short_and_trade_direction: `Alias of the short-window 3-way combined slice: regime + ${short} + trade direction.`,
    session_and_trade_direction:
      "Marginal cohort by trading session at entry × trade side (long/short). Keys: session_id|trade_direction.",
    session_and_candle_direction: `Marginal cohort at entry: trading session + ${long}.`,
    session_and_candle_direction_short: `Marginal cohort at entry: trading session + ${short}.`,
    session_and_market_condition:
      "Marginal cohort by session × candle regime (market_condition) at entry.",
    session_and_market_condition_and_trade_direction:
      "Marginal cohort by session × regime × trade side at entry.",
    session_and_market_condition_and_direction_and_trade_direction: `Marginal cohort at entry: trading session + regime + ${long} + trade direction.`,
    session_and_market_condition_and_direction_short_and_trade_direction: `Marginal cohort at entry: trading session + regime + ${short} + trade direction.`,
  };
}

/** @deprecated Prefer sliceDimensionDefinitionFallbacks(candleTimeframe). */
export const SLICE_DIMENSION_DEFINITION_FALLBACKS: Record<string, string> =
  sliceDimensionDefinitionFallbacks();

/** Value-level glossary entries (single-slice values). */
export function sliceValueDefinitionFallbacks(
  candleTimeframe?: string | null
): Record<string, string> {
  const longWin = formatDirectionBarsWindow("long", candleTimeframe);
  const shortWin = formatDirectionBarsWindow("short", candleTimeframe);
  const deadband = MARKET_REGIME_DIRECTION_DEADBAND_PCT;
  const horizonNote = `see surrounding slice for ${longWin} vs ${shortWin}; |Δ%| < ${deadband} → flat`;
  return {
    uptrend:
      "Uptrend: higher highs / higher lows with positive follow-through (classifier v3).",
    downtrend:
      "Downtrend: lower highs / lower lows with negative follow-through (classifier v3).",
    range: "Range: sideways, no sustained directional progress.",
    choppy: "Choppy: sharp swings, poor follow-through / frequent reversals.",
    unknown: "Unknown: not enough history or data to classify the regime.",
    accumulation:
      "Accumulation regime (legacy): the market is compressing and rotating in a base-like zone before a possible directional move.",
    trend: "Trend regime (legacy): the market is moving directionally with sustained follow-through.",
    expansion: "Expansion regime (legacy): volatility and directional movement are elevated versus recent conditions.",
    volatile: "Volatile regime (legacy): elevated volatility versus recent conditions.",
    up: `Market candle direction up: net price movement from window start to end was upward (${horizonNote}).`,
    down: `Market candle direction down: net price movement from window start to end was downward (${horizonNote}).`,
    flat: `Market candle direction flat: net price movement from window start to end was within the deadband (${horizonNote}).`,
    long: "Trade direction long: these are buy-side positions.",
    short: "Trade direction short: these are sell-side positions.",
  };
}

/** @deprecated Prefer sliceValueDefinitionFallbacks(candleTimeframe). */
export const SLICE_VALUE_DEFINITION_FALLBACKS: Record<string, string> =
  sliceValueDefinitionFallbacks();

export function buildCombinedSliceDefinitionFallbacks(
  node: ConditionAnalysisNode | null,
  candleTimeframe?: string | null
): Record<string, string> {
  const rs = node?.regime_summary;
  if (!rs) return {};
  const long = marketCandleDirectionLabel("long", candleTimeframe);
  const short = marketCandleDirectionLabel("short", candleTimeframe);
  const out: Record<string, string> = {};
  const add = (key: string, value: string): void => {
    if (!key || out[key]) return;
    out[key] = value;
  };

  for (const row of rs.market_condition_and_direction ?? []) {
    const [mc, cd] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (mc && cd) {
      add(row.key, `Trades where the market regime was ${mc} and ${long} was ${cd} at entry.`);
    }
  }

  for (const row of rs.candle_direction_and_trade_direction ?? []) {
    const [cd, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (cd && td) add(row.key, `Trades where ${long} was ${cd} and you took ${td} positions.`);
  }

  for (const row of rs.candle_direction_short_and_trade_direction ?? []) {
    const [cd, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (cd && td) {
      add(row.key, `Trades where ${short} was ${cd} and you took ${td} positions.`);
    }
  }

  for (const row of rs.market_condition_candle_direction_and_trade_direction ?? []) {
    const [mc, cd, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (mc && cd && td) {
      add(row.key, `Trades where the regime was ${mc}, ${long} was ${cd}, and you took ${td} positions.`);
    }
  }

  for (const row of rs.market_condition_candle_direction_short_and_trade_direction ?? []) {
    const [mc, cd, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (mc && cd && td) {
      add(row.key, `Trades where the regime was ${mc}, ${short} was ${cd}, and you took ${td} positions.`);
    }
  }

  const sessLabel = (sid: string) => sid.replace(/_/g, " ");
  for (const row of rs.session_and_trade_direction ?? []) {
    const [sid, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (sid && td) add(row.key, `Trades in session ${sessLabel(sid)} taken ${td}.`);
  }
  for (const row of rs.session_and_candle_direction ?? []) {
    const [sid, cd] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (sid && cd) add(row.key, `Trades in session ${sessLabel(sid)} with ${long} ${cd} at entry.`);
  }
  for (const row of rs.session_and_candle_direction_short ?? []) {
    const [sid, cd] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (sid && cd) add(row.key, `Trades in session ${sessLabel(sid)} with ${short} ${cd} at entry.`);
  }
  for (const row of rs.session_and_market_condition ?? []) {
    const [sid, mc] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (sid && mc) add(row.key, `Trades in session ${sessLabel(sid)} with regime ${mc} at entry.`);
  }
  for (const row of rs.session_and_market_condition_and_trade_direction ?? []) {
    const [sid, mc, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (sid && mc && td) add(row.key, `Trades in session ${sessLabel(sid)}, regime ${mc}, side ${td} at entry.`);
  }
  for (const row of rs.session_and_market_condition_and_direction_and_trade_direction ?? []) {
    const [sid, mc, cd, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (sid && mc && cd && td) {
      add(row.key, `Trades in session ${sessLabel(sid)}, regime ${mc}, ${long} ${cd}, side ${td} at entry.`);
    }
  }
  for (const row of rs.session_and_market_condition_and_direction_short_and_trade_direction ?? []) {
    const [sid, mc, cd, td] = String(row.key ?? "").split("|").map((x) => x?.trim());
    if (sid && mc && cd && td) {
      add(row.key, `Trades in session ${sessLabel(sid)}, regime ${mc}, ${short} ${cd}, side ${td} at entry.`);
    }
  }

  return out;
}

export function mergedMetricDefinitionMap(
  defs: Record<string, unknown> | undefined,
  node: ConditionAnalysisNode | null,
  candleTimeframe?: string | null
): Record<string, unknown> {
  return {
    ...sliceDimensionDefinitionFallbacks(candleTimeframe),
    ...sliceValueDefinitionFallbacks(candleTimeframe),
    ...buildCombinedSliceDefinitionFallbacks(node, candleTimeframe),
    ...(defs ?? {}),
  };
}

function metricDefinitionOrderKey(metricKey: string): [number, number, string] {
  const key = String(metricKey || "").trim();
  const lower = key.toLowerCase();
  const isCombined = key.includes("|") || lower.includes("_and_") || lower.includes("_x_");
  if (isCombined) return [2, 99, lower];

  const singleOrder: Record<string, number> = {
    market_condition: 0,
    regime: 1,
    candle_direction: 2,
    candle_direction_short: 3,
    trade_direction: 4,
    session: 5,
    direction: 6,
    accumulation: 10,
    trend: 11,
    range: 12,
    expansion: 13,
    up: 20,
    down: 21,
    flat: 22,
    long: 30,
    short: 31,
    adx: 40,
    atr_regime: 41,
    consistency: 42,
    htf: 43,
    htf_weighted: 44,
    sr: 45,
    sr_room: 46,
    sr_strength: 47,
    squeeze: 48,
    bb_zone: 49,
    ma50: 50,
    ma200: 51,
  };
  if (Object.prototype.hasOwnProperty.call(singleOrder, lower)) {
    return [0, singleOrder[lower], lower];
  }
  return [1, 99, lower];
}

export function sortMetricDefinitionKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const oa = metricDefinitionOrderKey(a);
    const ob = metricDefinitionOrderKey(b);
    if (oa[0] !== ob[0]) return oa[0] - ob[0];
    if (oa[1] !== ob[1]) return oa[1] - ob[1];
    return oa[2].localeCompare(ob[2]);
  });
}

export type PickTitleSegment = {
  label: string;
  definitionKey: string;
};

const TABLE_PART_DIMENSION_FALLBACKS: Record<string, string[]> = {
  market_condition_and_direction: ["market_condition", "candle_direction"],
  market_condition_and_direction_short: ["market_condition", "candle_direction_short"],
  candle_direction_and_trade_direction: ["candle_direction", "trade_direction"],
  candle_direction_short_and_trade_direction: ["candle_direction_short", "trade_direction"],
  market_condition_candle_direction_and_trade_direction: ["market_condition", "candle_direction", "trade_direction"],
  market_condition_candle_direction_short_and_trade_direction: [
    "market_condition",
    "candle_direction_short",
    "trade_direction",
  ],
  session_and_trade_direction: ["session", "trade_direction"],
  session_and_candle_direction: ["session", "candle_direction"],
  session_and_candle_direction_short: ["session", "candle_direction_short"],
  session_and_market_condition: ["session", "market_condition"],
  session_and_market_condition_and_trade_direction: ["session", "market_condition", "trade_direction"],
  session_and_market_condition_and_direction_and_trade_direction: [
    "session",
    "market_condition",
    "candle_direction",
    "trade_direction",
  ],
  session_and_market_condition_and_direction_short_and_trade_direction: [
    "session",
    "market_condition",
    "candle_direction_short",
    "trade_direction",
  ],
};

function tokenLabel(token: string, tableKey: string, index: number): string {
  const t = token.trim();
  if (!t) return t;
  if (tableKey.startsWith("session") && index === 0) return t.replace(/_/g, " ");
  return t;
}

function segmentsFromRowKey(tableKey: string, rowKey: string): PickTitleSegment[] | null {
  const parts = rowKey.split("|").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    return [{ label: tokenLabel(parts[0], tableKey, 0), definitionKey: parts[0] }];
  }
  return parts.map((part, index) => ({
    label: tokenLabel(part, tableKey, index),
    definitionKey: part,
  }));
}

function segmentsFromSliceContext(pick: ReportPick): PickTitleSegment[] | null {
  const ctx = pick.sliceContext;
  if (!ctx) return null;
  const tok = (s: string) => s.replace(/_/g, " ");
  const out: PickTitleSegment[] = [];
  if (ctx.session_id) out.push({ label: tok(ctx.session_id), definitionKey: ctx.session_id });
  if (ctx.market_condition) out.push({ label: tok(ctx.market_condition), definitionKey: ctx.market_condition });
  if (ctx.candle_direction) out.push({ label: ctx.candle_direction, definitionKey: ctx.candle_direction });
  if (ctx.trade_direction) out.push({ label: ctx.trade_direction, definitionKey: ctx.trade_direction });
  return out.length > 0 ? out : null;
}

/** Clickable title parts for segment picks (marginal sub-tables and configured slices). */
export function getPickTitleSegments(pick: ReportPick): PickTitleSegment[] | null {
  if (pick.kind !== "segment") return null;

  const tableKey = typeof pick.meta?.summary_table === "string" ? pick.meta.summary_table.trim() : "";
  const rowKey = typeof pick.meta?.row_key === "string" ? pick.meta.row_key.trim() : "";

  if (tableKey && rowKey) {
    return segmentsFromRowKey(tableKey, rowKey);
  }

  const fromCtx = segmentsFromSliceContext(pick);
  if (fromCtx && fromCtx.length > 1) return fromCtx;
  if (fromCtx?.length === 1) return fromCtx;

  const labelMain = pick.label.split(" · ")[0]?.trim() ?? "";
  if (labelMain.includes("|")) {
    const pseudoKey = labelMain.replace(/\s*\([^)]*\)\s*/g, "").trim();
    const parts = pseudoKey.split("|").map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      return parts.map((part, index) => ({
        label: tokenLabel(part, "", index),
        definitionKey: part.replace(/\s+/g, "_").toLowerCase(),
      }));
    }
  }

  return null;
}

/** Optional trailing context after ` · ` in pick labels (configured slices). */
export function getPickTitleLabelSuffix(pick: ReportPick): string | null {
  const idx = pick.label.indexOf(" · ");
  if (idx < 0) return null;
  const suffix = pick.label.slice(idx).trim();
  return suffix || null;
}

/** Best glossary row key to scroll to for a clicked segment. */
export function resolveDefinitionMetricKey(
  segment: PickTitleSegment,
  partIndex: number,
  pick: ReportPick,
  defs: Record<string, unknown>
): string {
  const k = segment.definitionKey.trim();
  if (k in defs) return k;

  const tableKey = typeof pick.meta?.summary_table === "string" ? pick.meta.summary_table.trim() : "";
  const rowKey = typeof pick.meta?.row_key === "string" ? pick.meta.row_key.trim() : "";
  if (rowKey && rowKey in defs) return rowKey;

  const dims = tableKey ? TABLE_PART_DIMENSION_FALLBACKS[tableKey] : undefined;
  const dimKey = dims?.[partIndex];
  if (dimKey && dimKey in defs) return dimKey;

  if (k === "long" || k === "short") {
    if (k in defs) return k;
    if ("trade_direction" in defs) return "trade_direction";
  }

  if (tableKey.startsWith("session") && partIndex === 0 && "session" in defs) return "session";
  if ((k === "up" || k === "down" || k === "flat") && "candle_direction" in defs) return "candle_direction";

  return k;
}

export function buildMetricDefinitionRows(
  defs: Record<string, unknown> | undefined,
  node: ConditionAnalysisNode | null
): Array<{ metric: string; definition: string }> {
  const merged = mergedMetricDefinitionMap(defs, node);
  return sortMetricDefinitionKeys(Object.keys(merged)).map((metric) => ({
    metric,
    definition: toNaturalMetricDefinition(merged[metric]),
  }));
}
